import Button from "@material-ui/core/Button";
import InputAdornment from "@material-ui/core/InputAdornment";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import LockIcon from "@material-ui/icons/LockOutlined";
import PersonIcon from "@material-ui/icons/PersonOutline";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { BrowserRouter as Router } from "react-router-dom";

import { UserContext } from "../contexts/UserContext";
import KeyboardEventHandler from "react-keyboard-event-handler";

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: "100vh",
    width: "100%",
    display: "grid",
    placeItems: "center",
    position: "relative",
    padding: theme.spacing(2),
    boxSizing: "border-box",
    backgroundColor: "#05080f",
    backgroundImage:
      "radial-gradient(ellipse 60% 45% at 50% -8%, rgba(167,139,250,0.16), transparent), " +
      "radial-gradient(ellipse 40% 35% at 0% 100%, rgba(129,140,248,0.10), transparent)",
  },
  card: {
    width: "100%",
    maxWidth: 420,
    padding: "40px 36px 30px",
    borderRadius: 20,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: "rgba(13,21,38,0.85)",
    border: "1px solid rgba(148,163,184,0.16)",
    backdropFilter: "blur(14px)",
    boxShadow: "0 30px 80px rgba(0,0,0,0.5)",
    color: "#e6edf6",
  },
  logo: {
    width: 72,
    height: 72,
    borderRadius: 16,
    boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
  },
  title: {
    marginTop: 18,
    fontSize: "1.45rem",
    fontWeight: 500,
    color: "#e6edf6",
    textAlign: "center",
    lineHeight: 1.2,
  },
  titleAccent: {
    fontWeight: 700,
    color: "#a78bfa",
  },
  subtitle: {
    marginTop: 6,
    marginBottom: theme.spacing(4),
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "#8b98ab",
    textAlign: "center",
  },
  field: {
    width: "100%",
    marginBottom: theme.spacing(2.5),
    "& .MuiOutlinedInput-root": {
      color: "#e6edf6",
      backgroundColor: "rgba(148,163,184,0.07)",
      borderRadius: 12,
      "& fieldset": {
        borderColor: "rgba(148,163,184,0.18)",
        borderRadius: 12,
      },
      "&:hover fieldset": {
        borderColor: "rgba(148,163,184,0.35)",
      },
      "&.Mui-focused": {
        boxShadow: "0 0 0 3px rgba(167,139,250,0.18)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#a78bfa",
        borderWidth: 1,
      },
    },
    "& .MuiInputLabel-outlined": {
      color: "#8b98ab",
    },
    "& .MuiInputLabel-outlined.Mui-focused": {
      color: "#a78bfa",
    },
    "& .MuiInputAdornment-root .MuiSvgIcon-root": {
      color: "#5c6b80",
    },
    "& .MuiFormHelperText-root.Mui-error": {
      color: "#f87171",
    },
    "& .MuiOutlinedInput-root.Mui-error fieldset": {
      borderColor: "#f87171",
    },
    // Chrome autofill pinta los inputs de blanco; se cubre con sombra interna
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus":
      {
        WebkitBoxShadow: "0 0 0 100px #111a2c inset",
        WebkitTextFillColor: "#e6edf6",
        caretColor: "#e6edf6",
        borderRadius: "inherit",
      },
  },
  button: {
    width: "100%",
    marginTop: theme.spacing(1),
    padding: 13,
    fontWeight: 700,
    borderRadius: 12,
    textTransform: "none",
    fontSize: 16,
    color: "#ffffff",
    background: "linear-gradient(120deg, #a78bfa, #7c3aed)",
    boxShadow: "none",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      background: "linear-gradient(120deg, #a78bfa, #7c3aed)",
      transform: "translateY(-1px)",
      boxShadow: "0 8px 26px rgba(167,139,250,0.35)",
    },
  },
  loaderWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    marginTop: theme.spacing(1),
    minHeight: 50,
  },
  spinner: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    border: "3px solid rgba(167,139,250,0.25)",
    borderTopColor: "#a78bfa",
    animation: "$spin 0.8s linear infinite",
  },
  "@keyframes spin": {
    to: { transform: "rotate(360deg)" },
  },
  demoBox: {
    width: "100%",
    marginTop: theme.spacing(2.5),
    background: "rgba(167,139,250,0.06)",
    border: "1px dashed rgba(167,139,250,0.35)",
    borderRadius: 12,
    padding: "10px 12px",
    cursor: "pointer",
    textAlign: "center",
    transition: "border-color 0.15s ease",
    "&:hover": {
      borderColor: "#a78bfa",
      borderStyle: "solid",
    },
  },
  demoLabel: {
    fontSize: "0.68rem",
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    color: "#a78bfa",
    fontWeight: 700,
  },
  demoCreds: {
    fontSize: "0.85rem",
    color: "#e6edf6",
    marginTop: 2,
    // index.css fuerza "Noto Sans" con !important sobre *; se contrarresta aquí
    "& span": {
      fontFamily:
        "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace !important",
    },
  },
  demoHint: {
    fontSize: "0.7rem",
    color: "#5c6b80",
    marginTop: 2,
  },
  footer: {
    marginTop: 26,
    fontSize: "0.75rem",
    color: "#5c6b80",
    textAlign: "center",
  },
}));

export default function Login() {
  const classes = useStyles();
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [isLogged, setIsLogged] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ipAddress, setIpAddress] = useState("");
  const [userNameError, setUserNameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);

  const handleChangUserName = (event) => {
    setUserName(event.target.value);
    if (userNameError) setUserNameError(false);
  };

  const handleChangePassword = (event) => {
    setPassword(event.target.value);
    if (passwordError) setPasswordError(false);
  };

  useEffect(() => {
    const fetchIpAddress = async () => {
      try {
        const response = await fetch("https://api.ipify.org");
        const text = await response.text();
        setIpAddress(text);
      } catch (error) {
        console.error("Error fetching IP address:", error);
      }
    };

    fetchIpAddress();
  }, []);

  const searchLoginValues = () => {
    const isUserNameEmpty = !userName.trim();
    const isPasswordEmpty = !password.trim();
    if (isUserNameEmpty || isPasswordEmpty) {
      setUserNameError(isUserNameEmpty);
      setPasswordError(isPasswordEmpty);
      return;
    }

    setIsLoading(true);
    let loginRequest = {
      PhoneLine: "1234",
      LoginName: userName,
      Password: password,
      IP: "192.168.3.99",
    };
    axios({
      method: "post",
      url: process.env.REACT_APP_API2_URL + "/Login/Login",
      data: loginRequest,
    }).then((response) => {
      if (response.data.userId) {
        const urlresponseBooks =
          process.env.REACT_APP_API2_URL +
          "/User/UsersBooks?idUser=" +
          response.data.userId;

        axios({
          method: "get",
          url: urlresponseBooks,
        }).then((responseBooks) => {
          response.data.responseBooks = responseBooks.data;

          let sessionToStore = response.data;

          let commaSeparatedBooks = response.data.responseBooks.map(
            (v) => v.IdBook
          );
          commaSeparatedBooks = commaSeparatedBooks.join();

          sessionToStore.commaSeparatedBooks = commaSeparatedBooks;
          sessionToStore.IP = ipAddress;

          localStorage.setItem("userInfo", JSON.stringify(sessionToStore));
          setIsLogged(true);
        });
      } else {
        setIsLoading(false);
        alert("User or Password wrong!");
      }
    });
  };

  const setEventKey = function (key, e) {
    switch (key) {
      case "enter":
        e.preventDefault();
        searchLoginValues();
        break;
      default:
        break;
    }
  };

  return (
    <Router>
      <UserContext.Provider value="Hellos">
        <KeyboardEventHandler
          handleKeys={["enter"]}
          onKeyEvent={(key, e) => setEventKey(key, e)}
        />
        <div
          className={classes.page}
          style={isLogged ? { visibility: "hidden" } : undefined}
        >
          <Paper elevation={0} className={classes.card}>
            <img
              src={process.env.PUBLIC_URL + "/nexus-manager.svg"}
              alt="Nexus Manager"
              className={classes.logo}
            />
            <Typography variant="h5" component="h1" className={classes.title}>
              Nexus <span className={classes.titleAccent}>Manager</span>
            </Typography>
            <Typography component="p" className={classes.subtitle}>
              Nexus Gaming Solutions
            </Typography>
            <TextField
              className={classes.field}
              variant="outlined"
              size="small"
              type="text"
              label="User Name"
              value={userName}
              onChange={handleChangUserName}
              error={userNameError}
              helperText={userNameError ? "User is required" : ""}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              className={classes.field}
              variant="outlined"
              size="small"
              type="password"
              label="Password"
              value={password}
              onChange={handleChangePassword}
              error={passwordError}
              helperText={passwordError ? "Password is required" : ""}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            {isLoading ? (
              <div className={classes.loaderWrap}>
                <div className={classes.spinner} />
              </div>
            ) : (
              <Button
                variant="contained"
                className={classes.button}
                onClick={searchLoginValues}
              >
                Login
              </Button>
            )}
            <button
              type="button"
              className={classes.demoBox}
              onClick={() => {
                setUserName("test16");
                setPassword("12345");
                setUserNameError(false);
                setPasswordError(false);
              }}
            >
              <div className={classes.demoLabel}>Demo Access</div>
              <div className={classes.demoCreds}>
                <span>test16 / 12345</span>
              </div>
              <div className={classes.demoHint}>Click to autofill</div>
            </button>
            <Typography component="p" className={classes.footer}>
              Secure login &middot; &copy; 2026 Nexus Gaming Solutions
            </Typography>
          </Paper>
        </div>
      </UserContext.Provider>
      {isLogged ? window.location.replace("/home") : ""}
    </Router>
  );
}
